import * as Sentry from '@sentry/react';

export type SafeReadOperation = 'profile-read' | 'store-read' | 'party-read';
export type TransportPhase = 'fetch' | 'response-body' | 'response-parse';
type FailureType = 'TypeError' | 'AbortError' | 'deadline' | 'SyntaxError' | 'other' | 'content-type';

export interface TransportDiagnostics {
  revision?: 1;
  request_id?: string;
  phase?: TransportPhase;
  failure_type?: FailureType;
  response_received?: boolean;
  response_status?: number;
  elapsed_ms?: number;
  connectivity?: 'online' | 'offline' | 'unknown';
  visibility?: 'visible' | 'hidden' | 'unknown';
  operation?: SafeReadOperation;
  attempts?: 1 | 2;
  recovery_elapsed_ms?: number;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  mime?: 'json' | 'html' | 'other' | 'missing';
  encoding?: 'gzip' | 'br' | 'deflate' | 'identity' | 'other' | 'missing';
  declared_body_bytes?: number;
  decoded_body_chars?: '0' | '1-1024' | '1025-16384' | '16385+';
  body_empty?: boolean;
}

// 진단 정보는 오류 메시지·응답 본문·고객 상태와 분리한다.
const diagnostics = new WeakMap<object, TransportDiagnostics>();
const duration = (ms: number) => Math.min(300_000, Math.max(0, Math.round(ms)));
export const READ_REQUEST_ID_HEADER = 'X-Olulo-Request-Id';

export function isReadRequestId(value: unknown): value is string {
  return typeof value === 'string' && /^mbr_[a-p]{32}$/.test(value);
}

export function createReadRequestId(): string | undefined {
  try {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    // 난수 ID가 PII 숫자 패턴으로 정제되지 않도록 영문자만 사용한다.
    return `mbr_${Array.from(bytes, byte => 'abcdefghijklmnop'[byte >> 4] + 'abcdefghijklmnop'[byte & 15]).join('')}`;
  } catch { return undefined; }
}

export function annotateTransportFailure(
  error: Error,
  phase: TransportPhase,
  failureType: FailureType,
  elapsedMs: number,
  responseStatus?: number,
  requestId?: string,
): void {
  try {
    const connectivity = typeof navigator === 'undefined'
      ? 'unknown'
      : navigator.onLine === true ? 'online' : navigator.onLine === false ? 'offline' : 'unknown';
    const state = typeof document === 'undefined' ? undefined : document.visibilityState;
    diagnostics.set(error, {
      revision: 1,
      ...(isReadRequestId(requestId) ? { request_id: requestId } : {}),
      phase,
      failure_type: failureType,
      response_received: responseStatus !== undefined,
      ...(responseStatus !== undefined ? { response_status: responseStatus } : {}),
      elapsed_ms: duration(elapsedMs),
      connectivity,
      visibility: state === 'visible' || state === 'hidden' ? state : 'unknown',
    });
  } catch { /* 진단 실패가 원래 요청 오류를 덮어쓰지 않도록 한다. */ }
}

export function getTransportDiagnostics(error: unknown): TransportDiagnostics | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const value = diagnostics.get(error);
  return value ? { ...value } : undefined;
}

/** 본문이나 parser message는 보관하지 않고 응답의 형태만 기록한다. */
export function annotateParseFailure(error: Error, response: Response, cause: unknown, text?: string): void {
  const mime = response.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase();
  const encoding = response.headers.get('content-encoding')?.trim().toLowerCase();
  const length = response.headers.get('content-length');
  const bytes = length && /^\d{1,9}$/.test(length) ? Number(length) : undefined;
  diagnostics.set(error, {
    revision: 1,
    phase: 'response-parse',
    failure_type: cause === 'content-type' ? 'content-type' : cause instanceof SyntaxError ? 'SyntaxError' : 'other',
    response_received: true,
    response_status: response.status,
    mime: !mime ? 'missing' : /^application\/(?:[\w.+-]+\+)?json$/.test(mime) ? 'json' : mime === 'text/html' ? 'html' : 'other',
    encoding: !encoding ? 'missing' : encoding === 'gzip' || encoding === 'br' || encoding === 'deflate' || encoding === 'identity' ? encoding : 'other',
    ...(bytes !== undefined ? { declared_body_bytes: bytes } : {}),
    ...(text !== undefined ? {
      decoded_body_chars: text.length === 0 ? '0' : text.length <= 1024 ? '1-1024' : text.length <= 16384 ? '1025-16384' : '16385+',
      body_empty: text.trim().length === 0,
    } : {}),
  });
}

export function annotateParseRequest(error: Error, method: string | undefined, elapsedMs: number, requestId?: string): void {
  diagnostics.set(error, {
    ...diagnostics.get(error),
    elapsed_ms: duration(elapsedMs),
    ...(isReadRequestId(requestId) ? { request_id: requestId } : {}),
    ...(method === 'GET' || method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE' ? { method } : {}),
  });
}

export function recordPartyParseFailure(error: Error): void {
  try {
    Sentry.addBreadcrumb({
      category: 'api.response',
      message: 'menu.waitlist-party.parse-failed',
      level: 'info',
      data: { ...getTransportDiagnostics(error), operation: 'party-read' },
    });
  } catch { /* 진단 기록이 복구 요청을 중단하지 않도록 한다. */ }
}

export function annotateReadRecovery(
  error: unknown,
  operation: SafeReadOperation,
  attempts: 1 | 2,
  elapsedMs: number,
): void {
  if (!error || typeof error !== 'object') return;
  diagnostics.set(error, {
    ...diagnostics.get(error),
    revision: 1,
    operation,
    attempts,
    recovery_elapsed_ms: duration(elapsedMs),
  });
}

export function recordReadRetrying(
  operation: SafeReadOperation,
  elapsedMs: number,
  firstError: unknown,
): void {
  try {
    Sentry.addBreadcrumb({
      category: 'api.recovery',
      message: operation === 'party-read' ? 'menu.waitlist-party.retrying' : 'menu.bootstrap-read.retrying',
      level: 'info',
      data: {
        ...getTransportDiagnostics(firstError),
        operation,
        attempts: 1,
        recovery_elapsed_ms: duration(elapsedMs),
      },
    });
  } catch { /* 진단 기록 실패가 제한된 복구를 중단하지 않도록 한다. */ }
}

export function recordReadRecovered(
  operation: SafeReadOperation,
  attempts: 1 | 2,
  elapsedMs: number,
  firstError: unknown,
): void {
  try {
    Sentry.addBreadcrumb({
      category: 'api.recovery',
      message: operation === 'party-read' ? 'menu.waitlist-party.recovered' : 'menu.bootstrap-read.recovered',
      level: 'info',
      data: {
        ...getTransportDiagnostics(firstError),
        operation,
        attempts,
        recovery_elapsed_ms: duration(elapsedMs),
      },
    });
  } catch { /* 진단 기록 실패가 복구된 요청을 실패로 바꾸지 않도록 한다. */ }
}
